import React from "react";
import Select from "react-select";
import { useNavigate, useParams } from "react-router-dom";

import BreadCrumb from "../../../Components/Common/BreadCrumb";
import { API } from "@/api/client";
import { benefitService } from "@/api/services/benefit.service";
import {
  BenefitIssueMode,
  BenefitRewardKind,
  BenefitRule,
  BenefitRulePayload,
  BenefitSimpleItem,
  BenefitTargetType,
  BenefitTriggerType,
} from "@/api/types/benefit";
import { Button } from "@/Components/ui";
import { dialogService } from "@/utils/dialogService";
import { findMissingRequiredFields, focusFirstMissingField, formatRequiredMessage } from "@/utils/formRequired";
import { useUnsavedChangesGuard } from "@/hooks/useUnsavedChangesGuard";

type SelectOption = {
  value: number | string;
  label: string;
};

const targetOptions = [
  { value: "package", label: "패키지" },
  { value: "ticket", label: "티켓" },
];

const triggerOptionMap: Record<BenefitTargetType, { value: BenefitTriggerType; label: string }[]> = {
  package: [
    { value: "payment_success", label: "결제 성공" },
    { value: "package_checked_in", label: "패키지 예약 입장 완료" },
  ],
  ticket: [
    { value: "payment_success", label: "결제 성공" },
    { value: "ticket_used", label: "티켓 사용 완료" },
  ],
};

const rewardOptions = [
  { value: "coupon", label: "할인쿠폰" },
  { value: "voucher", label: "무료쿠폰" },
  { value: "precoupon", label: "예약쿠폰" },
];

const issueModeOptions = [
  { value: "fixed", label: "고정 수량" },
  { value: "quantity_based", label: "수량 연동" },
];

const activeOptions = [
  { value: "true", label: "활성" },
  { value: "false", label: "비활성" },
];

const BenefitRuleForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const ruleId = Number(id || 0);

  const [name, setName] = React.useState("");
  const [isActive, setIsActive] = React.useState(true);
  const [targetType, setTargetType] = React.useState<BenefitTargetType>("package");
  const [triggerType, setTriggerType] = React.useState<BenefitTriggerType>("payment_success");
  const [rewardKind, setRewardKind] = React.useState<BenefitRewardKind>("coupon");
  const [issueMode, setIssueMode] = React.useState<BenefitIssueMode>("fixed");
  const [issueCount, setIssueCount] = React.useState(1);

  const [packageOptions, setPackageOptions] = React.useState<SelectOption[]>([]);
  const [ticketOptions, setTicketOptions] = React.useState<SelectOption[]>([]);
  const [couponOptions, setCouponOptions] = React.useState<SelectOption[]>([]);
  const [voucherOptions, setVoucherOptions] = React.useState<SelectOption[]>([]);
  const [precouponOptions, setPrecouponOptions] = React.useState<SelectOption[]>([]);

  const [selectedPackages, setSelectedPackages] = React.useState<SelectOption[]>([]);
  const [selectedTickets, setSelectedTickets] = React.useState<SelectOption[]>([]);
  const [selectedReward, setSelectedReward] = React.useState<SelectOption | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = React.useState(false);
  const markDirty = React.useCallback(() => setHasUnsavedChanges(true), []);
  const { confirmDiscardChanges } = useUnsavedChangesGuard({
    isDirty: hasUnsavedChanges,
    confirmNavigation: dialogService.confirm,
  });

  const mapItemsToOptions = React.useCallback((items: BenefitSimpleItem[] = []) => {
    return items.map((item) => ({ value: item.id, label: item.name }));
  }, []);

  const fillForm = React.useCallback(
    (data: BenefitRule) => {
      setName(data.name);
      setIsActive(data.is_active);
      setTargetType(data.target_type);
      setTriggerType(data.trigger_type);
      setRewardKind(data.reward_kind);
      setIssueMode(data.issue_mode);
      setIssueCount(data.issue_count);
      setSelectedPackages(mapItemsToOptions(data.package_items));
      setSelectedTickets(mapItemsToOptions(data.ticket_items));
      setSelectedReward(
        data.reward_item
          ? { value: data.reward_item.id, label: data.reward_item.name }
          : null
      );
    },
    [mapItemsToOptions]
  );

  const fetchOptions = React.useCallback(async () => {
    try {
      const [packageResponse, ticketResponse, couponResponse, voucherResponse, precouponResponse] = await Promise.all([
        API.get(`/package/items/`),
        API.get(`/ticket/products/?include_hidden=true`),
        API.get(`/coupon/products/?include_hidden=true`),
        API.get(`/voucher/?include_retired=true`),
        API.get(`/precoupon/precoupon/`),
      ]);

      setPackageOptions(
        (packageResponse.data || []).map((item: any) => ({ value: item.id, label: item["상품명"] || item.name }))
      );
      setTicketOptions(
        (ticketResponse.data || []).map((item: any) => ({ value: item.id, label: item.name }))
      );
      setCouponOptions(
        (couponResponse.data || []).map((item: any) => ({ value: item.id, label: item.title }))
      );
      setVoucherOptions(
        (voucherResponse.data || []).map((item: any) => ({ value: item.id, label: item.name }))
      );
      setPrecouponOptions(
        (precouponResponse.data || []).map((item: any) => ({ value: item.id, label: item.name }))
      );
    } catch (error: any) {
      await dialogService.handleAlert(error.message, { force: "error" });
    }
  }, []);

  React.useEffect(() => {
    fetchOptions();
  }, [fetchOptions]);

  React.useEffect(() => {
    if (ruleId === 0) {
      return;
    }
    benefitService
      .getRule(ruleId)
      .then((response) => fillForm(response.data))
      .catch(async (error: any) => await dialogService.handleAlert(error.message, { force: "error" }));
  }, [fillForm, ruleId]);

  React.useEffect(() => {
    const currentOptions = triggerOptionMap[targetType];
    if (!currentOptions.find((option) => option.value === triggerType)) {
      setTriggerType(currentOptions[0].value);
    }

    if (targetType === "package") {
      setSelectedTickets([]);
    } else {
      setSelectedPackages([]);
    }
  }, [targetType, triggerType]);

  const rewardSelectOptions = React.useMemo(() => {
    if (rewardKind === "coupon") {
      return couponOptions;
    }
    if (rewardKind === "voucher") {
      return voucherOptions;
    }
    return precouponOptions;
  }, [couponOptions, precouponOptions, rewardKind, voucherOptions]);

  const submit = async () => {
    const missingFields = findMissingRequiredFields([
      { label: "규칙명", value: name, elementId: "benefit-name" },
      { label: "발송 혜택", value: selectedReward?.value, elementId: "benefit-reward" },
    ]);
    if (missingFields.length > 0) {
      await dialogService.handleAlert(formatRequiredMessage(missingFields));
      focusFirstMissingField(missingFields);
      return;
    }

    const payload: BenefitRulePayload = {
      name,
      is_active: isActive,
      target_type: targetType,
      trigger_type: triggerType,
      reward_kind: rewardKind,
      issue_mode: issueMode,
      issue_count: Number(issueCount),
      packages: targetType === "package" ? selectedPackages.map((item) => Number(item.value)) : [],
      tickets: targetType === "ticket" ? selectedTickets.map((item) => Number(item.value)) : [],
      coupon: rewardKind === "coupon" ? Number(selectedReward?.value) : null,
      voucher: rewardKind === "voucher" ? Number(selectedReward?.value) : null,
      precoupon: rewardKind === "precoupon" ? Number(selectedReward?.value) : null,
    };

    try {
      const successMessage = ruleId === 0 ? "자동 혜택 규칙이 생성되었습니다." : "자동 혜택 규칙이 수정되었습니다.";
      if (ruleId === 0) {
        await benefitService.createRule(payload);
      } else {
        await benefitService.updateRule(ruleId, payload);
      }
      setHasUnsavedChanges(false);
      await dialogService.handleAlert(successMessage);
      navigate(`/benefit-rule-list`);
    } catch (error: any) {
      await dialogService.handleAlert(error.message, { force: "error" });
    }
  };

  return (
    <div className="page-content">
      <div className="container-fluid" onChangeCapture={markDirty} onInputCapture={markDirty}>
        <BreadCrumb title="자동 혜택 규칙" pageTitle="쿠폰/자동화" />
        <div className="card">
          <div className="card-header">
            <h4 className="card-title mb-0">{ruleId === 0 ? "자동 혜택 규칙 생성" : "자동 혜택 규칙 수정"}</h4>
          </div>
          <div className="card-body">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="benefit-name" className="form-label">규칙명</label>
                <input id="benefit-name" className="form-control" value={name} onChange={(e) => setName(e.target.value)} />
              </div>

              <div>
                <label className="form-label">상태</label>
                <Select
                  options={activeOptions}
                  value={activeOptions.find((option) => option.value === String(isActive))}
                  onChange={(option) => {
                    markDirty();
                    setIsActive(option?.value === "true");
                  }}
                />
              </div>

              <div>
                <label className="form-label">타겟</label>
                <Select
                  options={targetOptions}
                  value={targetOptions.find((option) => option.value === targetType)}
                  onChange={(option) => {
                    markDirty();
                    setTargetType(option?.value as BenefitTargetType);
                  }}
                />
              </div>

              <div>
                <label className="form-label">시점</label>
                <Select
                  options={triggerOptionMap[targetType]}
                  value={triggerOptionMap[targetType].find((option) => option.value === triggerType)}
                  onChange={(option) => {
                    markDirty();
                    setTriggerType(option?.value as BenefitTriggerType);
                  }}
                />
              </div>

              <div>
                <label className="form-label">혜택 종류</label>
                <Select
                  options={rewardOptions}
                  value={rewardOptions.find((option) => option.value === rewardKind)}
                  onChange={(option) => {
                    markDirty();
                    setRewardKind(option?.value as BenefitRewardKind);
                    setSelectedReward(null);
                  }}
                />
              </div>

              <div>
                <label className="form-label">발송 혜택</label>
                <Select
                  inputId="benefit-reward"
                  options={rewardSelectOptions}
                  value={selectedReward}
                  onChange={(option) => {
                    markDirty();
                    setSelectedReward(option as SelectOption | null);
                  }}
                />
              </div>

              <div>
                <label className="form-label">수량 방식</label>
                <Select
                  options={issueModeOptions}
                  value={issueModeOptions.find((option) => option.value === issueMode)}
                  onChange={(option) => {
                    markDirty();
                    setIssueMode(option?.value as BenefitIssueMode);
                  }}
                />
              </div>

              <div>
                <label className="form-label">기본 수량</label>
                <input
                  className="form-control"
                  type="number"
                  min={1}
                  value={issueCount}
                  onChange={(e) => setIssueCount(Number(e.target.value || 1))}
                />
                <small className="text-muted">
                  수량 연동이면 이벤트 수량에 이 값을 곱합니다.
                </small>
              </div>
            </div>

            <div className="mt-4">
              <label className="form-label">대상 {targetType === "package" ? "패키지" : "티켓"}</label>
              {targetType === "package" ? (
                <Select
                  isMulti
                  options={packageOptions}
                  value={selectedPackages}
                  onChange={(options) => {
                    markDirty();
                    setSelectedPackages((options || []) as SelectOption[]);
                  }}
                  placeholder="비워두면 사이트 전체 패키지에 적용됩니다"
                />
              ) : (
                <Select
                  isMulti
                  options={ticketOptions}
                  value={selectedTickets}
                  onChange={(options) => {
                    markDirty();
                    setSelectedTickets((options || []) as SelectOption[]);
                  }}
                  placeholder="비워두면 사이트 전체 티켓에 적용됩니다"
                />
              )}
            </div>

            <div className="d-flex gap-2 mt-4">
              <Button color="success" onClick={submit}>저장</Button>
              <Button color="light" onClick={async () => {
                if (!await confirmDiscardChanges()) return;
                navigate(`/benefit-rule-list`);
              }}>취소</Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BenefitRuleForm;
